import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../constants';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FileText, Shield, Scale } from 'lucide-react';

const TermsAcceptanceModal = ({ 
  isOpen, 
  onAccept, 
  onClose,
  canClose = false,  // If true, user can dismiss without accepting
  forceAccept = true // If true, blocks the UI until accepted
}) => {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);

  // Fetch current terms
  const fetchTerms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/terms/current`);
      setTerms(response.data);
    } catch (error) {
      console.error('Failed to fetch terms:', error);
      toast.error('Failed to load Terms and Conditions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTerms();
      setHasScrolledToBottom(false);
      setHasAgreed(false);
    }
  }, [isOpen, fetchTerms]);

  // Handle scroll to track if user has read to the bottom
  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    // Consider "read" if within 50px of bottom
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  };

  // Handle acceptance
  const handleAccept = async () => {
    if (!hasAgreed) {
      toast.error('Please check the box to confirm you agree to the terms');
      return;
    }

    try {
      setAccepting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/terms/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Terms and Privacy Policy accepted');
      if (onAccept) onAccept();
    } catch (error) {
      toast.error('Failed to accept terms. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (canClose && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={canClose ? handleClose : undefined}>
      <DialogContent 
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => !canClose && e.preventDefault()}
        onEscapeKeyDown={(e) => !canClose && e.preventDefault()}
        data-testid="terms-acceptance-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-[#1e3a5f]" />
            {terms?.title || 'Terms and Conditions'}
          </DialogTitle>
          <DialogDescription>
            {forceAccept 
              ? 'Please review and accept our Terms and Conditions and Privacy Policy to continue using the service.'
              : 'Please review our Terms and Conditions.'}
            {terms?.version && (
              <span className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded">
                Version {terms.version}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm bg-orange-50 border border-orange-200 rounded-md px-3 py-2 text-slate-700" data-testid="privacy-update-note">
          We have updated our{' '}
          <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#F97316] font-semibold underline" data-testid="modal-privacy-link">Privacy Policy</a>
          {' '}(October 2026) to cover Notices of Commencement, online notarization, county e-recording, owner and sales-rep accounts, and job applications.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Terms Content */}
            <ScrollArea 
              className="flex-1 border rounded-lg p-4 bg-white max-h-[50vh]"
              onScrollCapture={handleScroll}
            >
              {terms?.is_placeholder ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Terms and Conditions Coming Soon</p>
                  <p className="text-sm mt-2">
                    The full terms and conditions will be available shortly.
                  </p>
                </div>
              ) : (
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: terms?.content_html || '' }}
                />
              )}
            </ScrollArea>

            {/* Summary if provided */}
            {terms?.summary && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-sm text-blue-800">
                  <strong>Summary:</strong> {terms.summary}
                </p>
              </div>
            )}

            {/* Agreement checkbox */}
            <div className="flex items-start gap-3 mt-4 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="terms-agree"
                checked={hasAgreed}
                onCheckedChange={setHasAgreed}
                className="mt-0.5"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms-agree" className="text-sm leading-relaxed cursor-pointer">
                I have read and agree to the Terms and Conditions and the Privacy Policy. I understand that by checking this box,
                I am entering into a legally binding agreement with All American Notice.
              </Label>
            </div>

            {/* Scroll hint */}
            {!hasScrolledToBottom && !terms?.is_placeholder && (
              <p className="text-xs text-center text-amber-600 mt-2">
                ↓ Please scroll to read the complete terms
              </p>
            )}
          </>
        )}

        <DialogFooter className="mt-4">
          {canClose && (
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          )}
          <Button 
            onClick={handleAccept}
            disabled={!hasAgreed || accepting || loading}
            className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
            data-testid="accept-terms-btn"
          >
            {accepting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Accepting...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                I Accept
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
